import React, { useEffect, useRef } from 'react'
import { useFormikContext } from 'formik'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'

import { TokenListFields } from '../pathMapping/TokenListFields'

import { TcnSourceHelp } from './TcnSourceFields'
import {
  splitToken,
  joinToken,
  isQualifiedToken,
  isWholeValuePrefix,
  labelForPrefix,
  PATH_EXAMPLES,
  PREFIX_OPTION_GROUPS,
} from './tokenSources'

// The backend joins the resolved values with "+" (CAMPAIGN_FIELD_SEPARATOR in
// tcn.py). Kept in sync so the preview matches what actually gets sent.
const SEPARATOR = '+'

const FIELD = 'voip_campaign_name_fields'

// Every row must name a resolvable source. A half-filled row serialises as
// "clientInfo:" -- harmless to the backend, which skips a token with an empty path,
// but invalid enough to gate the submit button until it is finished.
export const CAMPAIGN_FIELDS_SCHEMA = Yup.array().of(
  Yup.string().test(
    'qualified-token',
    'Choose a source and enter a path (Call Direction and Hunt Group take no path)',
    isQualifiedToken
  )
)

// Map a legacy single `voip_campaign_name` string to the token list so an existing
// config's setting shows up in the rows on load.
export const seedFromLegacy = (legacy) => {
  if (isEmpty(legacy)) {
    return []
  }
  if (legacy === 'callDirection&huntGroup') {
    return ['callDirection', 'huntGroup']
  }
  return [legacy]
}

const previewLabel = (token) => {
  const { prefix, path } = splitToken(token)
  if (!prefix) {
    return token
  }
  return path ? `${labelForPrefix(prefix)}:${path}` : labelForPrefix(prefix)
}

const tcnTokenCodec = {
  split: splitToken,
  join: joinToken,
  isQualified: isQualifiedToken,
}

/**
 * Seed the token list from the legacy single-string field once the config loads.
 *
 * TCN-only, so it stays here rather than in the shared container: no other connector
 * has a scalar predecessor to migrate from.
 *
 * The latch is keyed on Formik's `initialValues` rather than being a per-mount
 * boolean, because the edit form is reinitialised more than once per visit:
 * IntegrationsSettings starts from redux's empty `integration`, then dispatches the
 * fetched config, then dispatches it AGAIN with a health score attached
 * (integrations.actions.js:62). Each dispatch changes initialValues, so
 * enableReinitialize resets values to the stored config -- wiping a seed that a
 * one-shot latch had already recorded as done, and leaving the rows empty for a
 * config that has only the legacy scalar. Re-seeding per load fixes that.
 *
 * Clearing every row still sticks within a session: that changes `values`, not
 * `initialValues`, so the latch holds. Across loads an empty list seeds again --
 * deliberate, since the backend falls back to the same scalar for an empty list, so
 * both states resolve to the same campaign name. Making empty stick would mean
 * clearing the scalar on save, which changes that fallback.
 */
const useLegacySeed = () => {
  const { values, initialValues, setFieldValue } = useFormikContext()
  const seededFor = useRef(null)

  const tokens = (Array.isArray(values[FIELD]) ? values[FIELD] : []).filter(
    (token) => typeof token === 'string'
  )
  const filledTokens = tokens.filter(isQualifiedToken)
  // The edit form mounts before the config loads; `type` is set once it has.
  const configLoaded = !isEmpty(values.type)
  const legacy = values.voip_campaign_name

  useEffect(() => {
    if (!configLoaded || seededFor.current === initialValues) {
      return
    }
    seededFor.current = initialValues
    if (!isEmpty(filledTokens)) {
      return
    }
    const seed = seedFromLegacy(legacy)
    if (!isEmpty(seed)) {
      setFieldValue(FIELD, seed)
    }
    // Never write the legacy `voip_campaign_name`: the backend still reads it as the
    // fallback, so it stays the legacy source of truth and is only read here to seed.
  }, [configLoaded, initialValues, filledTokens, legacy, setFieldValue])
}

/**
 * VOIP Campaign Name: an ordered list of value sources, concatenated with "+" by the
 * backend in row order.
 *
 * Stores the backend shape directly -- an array of "<source>:<path>" token strings --
 * so nothing serialises it on submit. Injected via the schema `raw`/`as` hatch, so it
 * reads and writes Formik through context.
 */
export const TcnCampaignNameFields = () => {
  useLegacySeed()

  return (
    <TokenListFields
      field={FIELD}
      label="VOIP Campaign Name"
      codec={tcnTokenCodec}
      sources={PREFIX_OPTION_GROUPS}
      examples={PATH_EXAMPLES}
      isWholeValue={isWholeValuePrefix}
      testIdPrefix="tcn-campaign"
      containerTestId="tcn-campaign-name"
      columnLabels={['Source', 'Path']}
      addLabel="Add source"
      removeLabel="Remove campaign name source"
      separator={SEPARATOR}
      previewLabel={previewLabel}
      help={<TcnSourceHelp />}
      caption={<>Multiple sources are joined with &ldquo;{SEPARATOR}&rdquo; in row order.</>}
    />
  )
}
